import { useEffect, useRef, useState } from "preact/hooks";
import { streamAiRewrite, type AiStreamEvent } from "./ai-stream";
import { stripCodeFence } from "./strip-code-fence";
import { MAX_USER_MESSAGE_LENGTH, type AiChatTurn } from "@shared/schemas";
import type { EditProposalTurn } from "../components/edit-proposal-card";

// Assistant turns split by mode. `chat` lives in the conversation as text;
// `edit` is a staged proposal that requires explicit user approval before it
// touches the document body.
export type ChatTurn =
  | { id: string; role: "user"; text: string }
  | {
      id: string;
      role: "assistant";
      mode: "chat";
      text: string;
      streaming: boolean;
    }
  | EditProposalTurn;

const HISTORY_TURN_CAP = 6;
const EDIT_HISTORY_PLACEHOLDER = "(前回ページ全体を生成しました)";

type Options = {
  projectId: string;
  slug: string;
  // Snapshot of the current draft body. Re-read at send() time so the user can
  // keep editing the doc while a previous turn is still settling.
  getDraftBody: () => string;
  // Called when the user accepts an edit proposal — pushes the proposed body
  // into the route's draft state.
  onApplyToDraft: (body: string) => void;
};

export type ChatStream = {
  chat: ChatTurn[];
  userInput: string;
  setUserInput: (v: string) => void;
  streaming: boolean;
  streamError: string | null;
  // True while waiting for the first mode event — used to show the typing
  // indicator before the assistant turn lands in `chat`.
  awaitingMode: boolean;
  send: () => Promise<void>;
  abort: () => void;
  applyEdit: (turnId: string) => void;
  discardEdit: (turnId: string) => void;
};

export function useChatStream({
  projectId,
  slug,
  getDraftBody,
  onApplyToDraft,
}: Options): ChatStream {
  const [chat, setChat] = useState<ChatTurn[]>([]);
  const [userInput, setUserInput] = useState("");
  const [streaming, setStreaming] = useState(false);
  const [streamError, setStreamError] = useState<string | null>(null);

  // rAF-throttled streaming state. Tokens accumulate in tokenBufRef; every
  // animation frame we patch the in-flight assistant turn. This avoids one
  // setState per token but still feels live.
  const tokenBufRef = useRef<string>("");
  const streamingTurnRef = useRef<{ id: string; mode: "chat" | "edit" } | null>(null);
  const rafIdRef = useRef<number | null>(null);
  const abortRef = useRef<AbortController | null>(null);

  function flushBuffer() {
    rafIdRef.current = null;
    const target = streamingTurnRef.current;
    if (!target) return;
    const buf = tokenBufRef.current;
    setChat((prev) =>
      prev.map((t) => {
        if (t.id !== target.id || t.role !== "assistant") return t;
        if (t.mode === "chat") return { ...t, text: buf };
        if (t.mode === "edit") return { ...t, proposedBody: buf };
        return t;
      }),
    );
  }
  function scheduleFlush() {
    if (rafIdRef.current !== null) return;
    rafIdRef.current = requestAnimationFrame(flushBuffer);
  }
  function cancelFlush() {
    if (rafIdRef.current !== null) {
      cancelAnimationFrame(rafIdRef.current);
      rafIdRef.current = null;
    }
  }

  // Reset + teardown when the edited document changes or the view unmounts.
  // preact-iso reuses this component across /pages/:slug navigations, so without
  // this an in-flight SSE would keep streaming (burning AI inference + Worker
  // time) and the previous page's conversation would bleed into the next page.
  useEffect(() => {
    return () => {
      abortRef.current?.abort();
      abortRef.current = null;
      cancelFlush();
      streamingTurnRef.current = null;
      tokenBufRef.current = "";
      setChat([]);
      setStreaming(false);
      setStreamError(null);
      setUserInput("");
    };
    // Setters are stable and cancelFlush only touches refs; this should only
    // re-run (tear down) when the target document changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId, slug]);

  async function send() {
    const message = userInput.trim();
    if (!message || streaming) return;
    if (message.length > MAX_USER_MESSAGE_LENGTH) {
      setStreamError(`メッセージは${MAX_USER_MESSAGE_LENGTH}文字以内で入力してください`);
      return;
    }
    const ac = new AbortController();
    abortRef.current = ac;
    setStreaming(true);
    setStreamError(null);
    setUserInput("");

    // Show the user's turn immediately. The assistant turn is appended as
    // soon as the server tells us which mode it picked.
    const userTurnId = crypto.randomUUID();
    setChat((prev) => [...prev, { id: userTurnId, role: "user", text: message }]);

    // Build history sent to the API. For chat-mode assistant turns we ship the
    // actual reply (capped); edit-mode turns are compressed because the body
    // would balloon the context window. User turns pass through.
    const apiHistory: AiChatTurn[] = chat
      .slice(-HISTORY_TURN_CAP)
      .map<AiChatTurn | null>((t) => {
        if (t.role === "user") return { role: "user", content: t.text };
        if (t.mode === "chat") {
          const text = t.text.trim();
          if (!text) return null;
          return { role: "assistant", content: text.slice(0, 2000) };
        }
        return { role: "assistant", content: EDIT_HISTORY_PLACEHOLDER };
      })
      .filter((t): t is AiChatTurn => t !== null);

    tokenBufRef.current = "";
    streamingTurnRef.current = null;

    const finalize = (
      errCode: string | null = null,
      opts?: { incomplete?: boolean; removeOptimisticUser?: boolean },
    ) => {
      // Stale guard: if this send was torn down by the [projectId, slug]
      // cleanup (which aborts and nulls abortRef), the shared refs/state may
      // already belong to a newer stream — the cleanup did all the resetting,
      // so touching them here would clobber the new stream (e.g. make its
      // 中止 button a no-op by nulling its AbortController).
      if (abortRef.current !== ac) return;
      cancelFlush();
      // `incomplete` = the stream ended without a server-confirmed done
      // (user abort, or the connection was cut). Either way the buffered body
      // is truncated.
      const failed = errCode !== null || opts?.incomplete === true;
      const target = streamingTurnRef.current;
      if (target) {
        const finalBuf = tokenBufRef.current;
        setChat((prev) =>
          prev.map((t) => {
            if (t.id !== target.id || t.role !== "assistant") return t;
            if (t.mode === "chat") {
              return { ...t, text: finalBuf, streaming: false };
            }
            // Strip stray code fences and whitespace from edit proposals.
            const cleaned = stripCodeFence(finalBuf).trim();
            return {
              ...t,
              proposedBody: cleaned ? cleaned + "\n" : finalBuf,
              streaming: false,
              // A truncated proposal must not stay one click away from
              // overwriting the draft — flip it to discarded. The partial
              // diff stays viewable on the card.
              status: failed && t.status === "pending" ? "discarded" : t.status,
            };
          }),
        );
      }
      if (opts?.removeOptimisticUser) {
        setChat((prev) => prev.filter((t) => t.id !== userTurnId));
      }
      streamingTurnRef.current = null;
      tokenBufRef.current = "";
      setStreaming(false);
      abortRef.current = null;
      if (errCode) setStreamError(errCode);
    };

    try {
      for await (const ev of streamAiRewrite({
        projectIdOrSlug: projectId,
        slug,
        body: getDraftBody(),
        history: apiHistory,
        userMessage: message,
        signal: ac.signal,
      })) {
        if (ev.kind === "mode") {
          const turnId = crypto.randomUUID();
          streamingTurnRef.current = { id: turnId, mode: ev.mode };
          setChat((prev) =>
            ev.mode === "chat"
              ? [
                  ...prev,
                  {
                    id: turnId,
                    role: "assistant",
                    mode: "chat",
                    text: "",
                    streaming: true,
                  },
                ]
              : [
                  ...prev,
                  {
                    id: turnId,
                    role: "assistant",
                    mode: "edit",
                    proposedBody: "",
                    streaming: true,
                    status: "pending",
                  },
                ],
          );
        } else if (ev.kind === "token") {
          // Tokens before mode shouldn't happen (server gates them), but be
          // defensive: drop them rather than crash.
          if (!streamingTurnRef.current) continue;
          tokenBufRef.current += ev.text;
          scheduleFlush();
        } else if (ev.kind === "done") {
          const finishReason = normalizeFinishReason(ev.finishReason);
          if (isLengthFinish(finishReason)) {
            finalize("出力が長すぎて途中で切れました", { incomplete: true });
          } else {
            finalize(null);
          }
          return;
        } else if (ev.kind === "error") {
          finalize(`${ev.code}: ${ev.message}`, {
            removeOptimisticUser: isHttp4xxError(ev),
          });
          return;
        }
      }
      // Stream ended without an explicit done/error: the server writes a
      // terminal event on every path it controls, so a bare EOF means the
      // connection was cut mid-stream — treat the buffer as truncated.
      finalize(null, { incomplete: true });
    } catch (e) {
      if ((e as Error).name === "AbortError") {
        finalize(null, { incomplete: true });
      } else {
        finalize((e as Error).message);
      }
    }
  }

  function abort() {
    abortRef.current?.abort();
  }

  function applyEdit(turnId: string) {
    const turn = chat.find(
      (t) => t.id === turnId && t.role === "assistant" && t.mode === "edit",
    );
    if (!turn || turn.role !== "assistant" || turn.mode !== "edit") return;
    if (turn.streaming || turn.status !== "pending") return;
    onApplyToDraft(turn.proposedBody);
    setChat((prev) =>
      prev.map((t) =>
        t.id === turnId && t.role === "assistant" && t.mode === "edit"
          ? { ...t, status: "applied" }
          : t,
      ),
    );
  }

  function discardEdit(turnId: string) {
    setChat((prev) =>
      prev.map((t) =>
        t.id === turnId && t.role === "assistant" && t.mode === "edit"
          ? { ...t, status: "discarded" }
          : t,
      ),
    );
  }

  // True while streaming hasn't received a mode event yet (so no assistant
  // turn is in `chat` to render a streaming indicator on).
  const awaitingMode = streaming && streamingTurnRef.current === null;

  return {
    chat,
    userInput,
    setUserInput,
    streaming,
    streamError,
    awaitingMode,
    send,
    abort,
    applyEdit,
    discardEdit,
  };
}

function normalizeFinishReason(reason: string | undefined): string {
  return (reason ?? "").toLowerCase();
}

function isLengthFinish(reason: string): boolean {
  return reason === "length" || reason === "max_tokens";
}

function isHttp4xxError(ev: Extract<AiStreamEvent, { kind: "error" }>): boolean {
  if (!ev.code.startsWith("http_")) return false;
  const status = Number(ev.code.slice(5));
  return Number.isInteger(status) && status >= 400 && status < 500;
}
